/* eslint-disable @typescript-eslint/member-ordering */
import { ID } from '@datorama/akita';
import { Observable } from 'rxjs';
import { DatePipe } from '@angular/common';

import { Column, Entity, OneToMany } from '../entity';
import { Result } from '../interfaces/helpers';
import { IEntity } from '../interfaces/models';
import { ColumnType, LanguagePage, PageTitles } from '../interfaces/types';
import { SalesEventService } from '../services/salesEvent';
import { TerritoryService } from '../services/territory';
import { VendorSalesRepService } from '../services/vendorSalesRep';
import { IRepRosterVendorGroup } from './repRoster.model';
import { SalesEvent } from './salesEvent.model';
import { Territory } from './territory.model';
import { VendorSalesRep } from './vendorSalesRep.model';
import { commonConstants } from '../constants/common.constants';

export interface IVendorGroup extends IEntity {
  vendorName: string;
  vendorAccountName: string;
  repCount: number;
  federationId: string;
  vendorGroupId?: string;
  vendorMemberId?: string;
  cpmsid: string;
  vendorSalesReps?: Observable<Result<VendorSalesRep>>;
  salesEvents?: Observable<Result<SalesEvent>>;
  territories?: Observable<Result<Territory>>;
}

@Entity({
  name: PageTitles.vendorGroup,
  language: LanguagePage.vendorGroup,
})
export class VendorGroup implements IVendorGroup {
  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 1,
    },
  })
  vendorName: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 2,
    },
    form: {
      defaultPosition: 1,
    },
  })
  vendorAccountName: string;

  @Column({
    type: ColumnType.number,
    table: {
      defaultPosition: 3,
    },
    form: {
      defaultPosition: 2,
    },
  })
  repCount: number;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 4,
      hide: true,
    },
  })
  createdBy: string;

  @Column({
    type: ColumnType.date,
    table: {
      defaultPosition: 5,
      hide: true,
    },
  })
  createdAt: Date;

  @Column({
    type: ColumnType.string,
    form: {
      defaultPosition: 3,
    },
  })
  get createdField(): string {
    const datepipe: DatePipe = new DatePipe('en-CA');
    const date = datepipe.transform(
      this.createdAt,
      commonConstants.DATE_FORMATS.MMMdd_YYYY_HHmm_a
    );

    return `${this.createdBy} ${date.toString()}`;
  }

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 6,
      hide: true,
    },
  })
  modifiedBy: string;

  @Column({
    type: ColumnType.date,
    table: {
      defaultPosition: 7,
      hide: true,
    },
  })
  modifiedAt: Date;

  @Column({
    type: ColumnType.string,
    form: {
      defaultPosition: 4,
    },
  })
  get modifiedField(): string {
    const datepipe: DatePipe = new DatePipe('en-CA');
    const date = datepipe.transform(
      this.modifiedAt,
      commonConstants.DATE_FORMATS.MMMdd_YYYY_HHmm_a
    );

    return `${this.modifiedBy} ${date.toString()}`;
  }

  @OneToMany({
    repository: VendorSalesRepService,
    expression: { vendorGroupId: (i: VendorGroup) => i.id },
  })
  vendorSalesReps: Observable<Result<VendorSalesRep>>;

  @OneToMany({
    repository: SalesEventService,
    expression: { vendorGroup: (i: VendorGroup) => [i.vendorName] },
  })
  salesEvents: Observable<Result<SalesEvent>>;

  @OneToMany({
    repository: TerritoryService,
    expression: { vendorGroup: (i: VendorGroup) => [i.vendorName] },
  })
  territories: Observable<Result<Territory>>;

  id: ID;
  federationId: string;
  href?: string;
  vendorGroupId?: string;
  vendorMemberId?: string;
  cpmsid: string;

  constructor(
    vendorGroup: IVendorGroup = {
      id: '',
      vendorName: '',
      vendorAccountName: '',
      repCount: 0,
      federationId: '',
      vendorGroupId: '',
      vendorMemberId: '',
      createdBy: '',
      createdAt: new Date(),
      modifiedBy: '',
      modifiedAt: new Date(),
      cpmsid: '',
    }
  ) {
    const {
      id,
      vendorName,
      vendorAccountName,
      repCount,
      federationId,
      vendorGroupId,
      vendorMemberId,
      createdBy,
      createdAt,
      modifiedBy,
      modifiedAt,
      cpmsid,
    } = vendorGroup;

    this.id = id;
    this.vendorName = vendorName;
    this.vendorAccountName = vendorAccountName;
    this.repCount = repCount;
    this.federationId = federationId;
    this.vendorGroupId = vendorGroupId;
    this.vendorMemberId = vendorMemberId;
    this.createdBy = createdBy;
    this.createdAt = createdAt;
    this.modifiedBy = modifiedBy;
    this.modifiedAt = modifiedAt;
    this.cpmsid = cpmsid;
  }

  static createFromDTO(record: IRepRosterVendorGroup): VendorGroup {
    return new VendorGroup({
      id: record.Id,
      vendorName: record.Name,
      vendorAccountName: record?.Vendor__c,
      repCount: record?.Rep_Count__c,
      createdBy: record?.CreatedBy?.Name,
      createdAt: record?.CreatedDate ? new Date(record.CreatedDate) : null,
      modifiedBy: record?.LastModifiedBy?.Name,
      modifiedAt: record?.LastModifiedDate
        ? new Date(record.LastModifiedDate)
        : null,
      federationId: record?.Federation_ID__c,
      vendorMemberId: record.Id,
      cpmsid: record.CPMS_ID__c,
    });
  }
}
